import { useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from '@lingui/macro';
import { useFormContext } from 'react-hook-form';

import {
    FormControl,
    FormControlLabel,
    FormLabel,
    InputAdornment,
    MenuItem,
    Radio,
    RadioGroup,
    TextField,
} from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';
import Search from '@material-ui/icons/Search';
import { makeStyles } from '@material-ui/core/styles';
import _ from 'lodash';
import FormSection from '../../form/FormSection';
import { LegacyIcon } from '../../icon';
import {
    adjustValues,
    assignFields,
    filterVoipAgentsAgents,
    getCallManagers,
    getOther,
    hasManager,
    isAddNew,
    PLACEHOLDERS,
    setConfigDefaults,
    setDifferentCallManagerSettings,
    setSameCallManagerSettings,
    updateDirectCallingSipUrl,
} from './VoipPulseTestConfigHelpers';
import { useDataService } from '../../../services/ApiDataService';
import useAlerts from '../../../stores/AlertsStore';
import {
    CALL_VIA_EXTENSION,
    CALL_VIA_OPTIONS,
    CALL_VIA_PHONE_NUMBER,
    CALL_VIA_SIP_USER_ID,
    CALLER,
    PHONE_ID,
    VOIP_PULSE_TEST_TYPE,
    WITH_NO_PREFS_CODECS_KEY_VALUE,
} from './VoipTestConstants';
import NgpSelect from '../../form/NgpSelect';
import { AUTO_COMPLETE_DEFAULT, GENERIC_REQUIRED_MESSAGE } from '../../../utilities/FormUtilities';
import NgpTextField from '../../form/NgpTextField';
import { VoipTestNowCallSettings } from './VoipTestNowCallSettings';

const CALL_VIA_HEAD_FIELD = 'testConfig.callVia.head';
const CALL_VIA_BODY_FIELD = 'testConfig.callVia.body';
const PHONE_NUMBER_FIELD = 'testConfig.phone';

export const nPointsListApiUrl = '/query/admin/agents/picker';
export const voipAgentQueryParams = {
    query: JSON.stringify({
        $or: [
            { 'voipConfiguration.registration.status': 'Registered' },
            { 'voipConfiguration.registration.status': 'Direct Calling' },
        ],
    }),
    voip: true,
};

export const useFilterStyles = makeStyles((theme) => ({
    row: {
        display: 'flex',
        gap: theme.spacing(2),
        width: '100%',
    },
    column: {
        flex: 1,
        minWidth: 0,
    },
    filterContainer: {
        padding: theme.spacing(1),
        '& .MuiInputBase-input': {
            fontSize: theme.typography.body2.fontSize,
        },
    },
    filterCount: {
        fontSize: theme.typography.body2.fontSize,
        padding: theme.spacing(0, 1),
    },
    filterLength: {
        fontWeight: 'bold',
    },
    agentLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        fontSize: '14px',
    },
    agentRadio: {
        padding: '0 4px',
        '& .MuiSvgIcon-root': {
            fontSize: '24px',
        },
    },
    agentListItem: {
        margin: 0,
        marginTop: 0,
        '& .MuiFormControlLabel-label': {
            fontSize: '20px',
        },
    },
    agentIcon: {
        fontSize: '18px',
        marginRight: '6px',
    },
    testSelect: {
        marginRight: '8px',

        '& .MuiSelect-select': {
            minWidth: 390,
        },
    },
    calleeInstructionsSection: {
        border: '1px solid rgba(0, 0, 0, 0.23)',
        padding: theme.spacing(2, 3),
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(3),

        '& > div': {
            marginBottom: 0,
            marginRight: 0,
        },
    },
    calleeInstructionsSectionError: {
        borderColor: theme.palette.error.main,
    },
    calleeInstructionsErrorText: {
        color: theme.palette.error.main,
        fontSize: theme.typography.caption.fontSize,
        marginTop: theme.spacing(-2),
        marginBottom: theme.spacing(2),
    },
}));

export function VoipPulseTestConfig({ assignTest, setAssignTest, testNow, reRun }) {
    const filterClasses = useFilterStyles();
    const dataService = useDataService();
    const [, /* Not used */ alertsActions] = useAlerts();
    const { setValue, watch } = useFormContext();
    const [placeholder, setPlaceholder] = useState({});
    const [disableCallViaBody, setDisableCallViaBody] = useState(true);
    const [showCallViaBody, setShowCallViaBody] = useState(false);
    const [agents, setAgents] = useState([]);
    const [callerFilterText, setCallerFilterText] = useState('');
    const [calleeFilterText, setCalleeFilterText] = useState('');
    const [voipAgentsById, setVoipAgentsById] = useState([]);
    const [voipConfig, setVoipConfig] = useState({});
    const [codecs, setCodecs] = useState(WITH_NO_PREFS_CODECS_KEY_VALUE);
    const firstLoadDone = useRef(false);
    const [selectedCallerId, setSelectedCallerId] = useState('');
    const [selectedCalleeId, setSelectedCalleeId] = useState('');
    const [callViaBodyInput, setCallViaBodyInput] = useState('');

    const callViaOptions = CALL_VIA_OPTIONS;
    const assignCallViaBody = _.get(assignTest, 'testConfig.callVia.body', '');

    useEffect(() => {
        (async () => {
            if (firstLoadDone.current) return;

            const test = assignTest;
            if (_.isEmpty(test)) return;

            if (isAddNew(reRun, test)) {
                test.testConfig = { ...test.testConfig, callee: { ids: [] } };
                test.testConfig = { ...test.testConfig, caller: { ids: [] } };

                test.agents = {
                    all: false,
                    ids: [],
                };
            }
            if (
                isAddNew(reRun, test) ||
                _.isUndefined(test.quickTestType) ||
                test.quickTestType === VOIP_PULSE_TEST_TYPE
            ) {
                if (!test.testConfig.callVia) {
                    test.testConfig.callVia = {};
                } else if (!_.isUndefined(test.testConfig.callVia.head)) {
                    setValue('showCallViaBody', true);
                }
            }

            // Unconditionally set agents.all = false
            _.set(test, 'agents.all', false);

            // Ensure agents.ids is set to [callerId, calleeId]
            if (_.isEmpty(test.agents.ids)) {
                test.agents.ids = [
                    _.get(test.testConfig, 'caller.ids.0.id'),
                    _.get(test.testConfig, 'callee.ids.0.id'),
                ];
            }

            setInitialUIElements(test);

            let agentData;
            if (testNow === true) {
                agentData = await dataService.get(nPointsListApiUrl, voipAgentQueryParams);
            } else {
                agentData = await dataService.get(nPointsListApiUrl);
            }

            const voipAgents = filterVoipAgentsAgents(testNow, agentData);
            let vAgentsById;
            vAgentsById = assignFields(voipAgents, test, testNow, alertsActions, setAgents);
            vAgentsById = await getCallManagers(voipAgents, dataService, vAgentsById);
            setConfigDefaults(
                assignTest,
                codecs,
                setCodecs,
                vAgentsById,
                setVoipConfig,
                setDisableCallViaBody,
                setShowCallViaBody
            );

            setVoipAgentsById(vAgentsById);
            setAssignTest(test);
            setValue('testConfig', test.testConfig);

            firstLoadDone.current = true;
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignTest, setValue]);

    useEffect(() => {
        // Always sync (including clearing) so the radio selection matches state
        // after an unselect (delete otherConfig.id) happens in selectAgent.
        setSelectedCallerId(voipConfig?.caller?.id ?? '');
        setSelectedCalleeId(voipConfig?.callee?.id ?? '');
    }, [voipConfig]);

    useEffect(() => {
        setCallViaBodyInput(assignCallViaBody || '');
    }, [assignCallViaBody]);

    function setInitialUIElements(test) {
        if (!_.get(test, CALL_VIA_HEAD_FIELD)) return;

        // need to set values in the UI if its an edit test
        // eslint-disable-next-line default-case
        switch (test.testConfig.callVia.head) {
            case CALL_VIA_EXTENSION:
                setPlaceholder(PLACEHOLDERS.EXTENSION);
                break;
            case CALL_VIA_PHONE_NUMBER:
                setPlaceholder(PLACEHOLDERS.PHONE_NUMBER);
                break;
            case CALL_VIA_SIP_USER_ID:
                setDisableCallViaBody(true);
                setPlaceholder(PLACEHOLDERS.USER_ID);
                break;
        }
    }

    const selectAgent = (e, calleeOrCaller) => {
        const id = e.target.value;

        const test = { ...assignTest };
        // Deep-ish clone so we don't mutate React state in place. Both caller/callee
        // sub-objects must be new references; otherwise subsequent updates can leave
        // stale values (e.g. callee.showDetails) when switching nPoints repeatedly.
        const vConfig = {
            ...voipConfig,
            caller: { ...(voipConfig.caller || {}) },
            callee: { ...(voipConfig.callee || {}) },
        };
        const config = vConfig[calleeOrCaller];
        const otherConfig = vConfig[getOther(calleeOrCaller)];

        config.id = id;
        config.showDetails = true;
        config.agent = voipAgentsById[id];

        // go check to see if the other select box has this agent selected
        // MDL Update - Two cases here, either they have only selected one of the two parties
        //  OR they have selected the same party for the caller and the callee
        //  either way the configuration is not valid
        if (_.isUndefined(otherConfig.id) || _.isUndefined(id) || otherConfig.id === id) {
            // unselect it
            delete otherConfig.id;
            delete otherConfig.agent;
            otherConfig.showDetails = false;

            // Because we need to know both the caller and the callee
            //  (and if we are here we don't have them) before we can give them the
            //  Call Via options, hide them until we have valid parties
            vConfig.callee.showDetails = false;
            // Also clear the phone flag; otherwise, after picking Phone as callee and then
            // re-selecting the same nPoint as caller, the Phone Number input would still show.
            vConfig.callee.phone = false;

            // we need to unselect the data from the otherConfig callVia if a new caller was set that
            // was originally the selected callee
            if (calleeOrCaller === CALLER) {
                test.testConfig.callVia = {};
            }

            // remove id from the config
            const otherTestConfig = test.testConfig[getOther(calleeOrCaller)];
            const otherTestConfigIds = _.get(otherTestConfig, 'ids', []);
            const idx = _.indexOf(_.map(otherTestConfigIds, 'id'), id);
            if (idx > -1) {
                otherTestConfigIds.splice(idx, 1);
            }
        }

        updateDirectCallingSipUrl(vConfig, test);

        // Verify that both selected agents has a callManager before calling other setup logic
        if (!hasManager(config, alertsActions) || !hasManager(otherConfig, alertsActions)) {
            return;
        }
        // Remove any no callmanager warnings if both our current selected pulses have managers
        alertsActions.handleRemove();

        // Setup call manager settings
        if (config.id && !_.isUndefined(otherConfig.id) && otherConfig.id) {
            _initCallManagerSettings();
        }

        setAssignTest(test);
        setVoipConfig(vConfig);

        function _initCallManagerSettings() {
            // We have two parties that are not the same, show the Call Via options
            vConfig.callee.showDetails = true;

            // if they have different call managers they can only call by phone
            if (
                _.get(config, 'agent.voipConfiguration.primaryCallManager._id') !==
                    _.get(otherConfig, 'agent.voipConfiguration.primaryCallManager._id') &&
                config.id !== PHONE_ID &&
                otherConfig.id !== PHONE_ID
            ) {
                // set only phone number being available
                setDifferentCallManagerSettings(vConfig.callee, test, false, setDisableCallViaBody, setShowCallViaBody);
            } else {
                setSameCallManagerSettings(test, setDisableCallViaBody, setShowCallViaBody, setPlaceholder);
            }

            // I believe that we only want to adjust values when we have both a Caller and a Callee selected.
            adjustValues(vConfig, config, otherConfig, calleeOrCaller, test, setShowCallViaBody);
        }
    };

    function changeCallVia(e, agent) {
        const callViaHead = e.target.value;

        let callViaBody;
        setDisableCallViaBody(false);
        setShowCallViaBody(true);

        const test = { ...assignTest };
        // eslint-disable-next-line default-case
        switch (callViaHead) {
            case CALL_VIA_EXTENSION:
                callViaBody = agent.voipConfiguration.extension;
                setPlaceholder(PLACEHOLDERS.EXTENSION);
                break;
            case CALL_VIA_PHONE_NUMBER:
                callViaBody = agent.voipConfiguration.phoneNumber;
                setPlaceholder(PLACEHOLDERS.PHONE_NUMBER);
                break;
            case CALL_VIA_SIP_USER_ID:
                setDisableCallViaBody(true);
                setPlaceholder(PLACEHOLDERS.USER_ID);
                callViaBody = agent.voipConfiguration.sipUserId;
                break;
        }

        test.testConfig.callVia.body = callViaBody;
        setCallViaBodyInput(callViaBody || '');
        setAssignTest(test);
    }

    useEffect(() => {
        if (showCallViaBody) {
            setValue('testConfig.callVia.body', assignTest.testConfig.callVia.body);
        }
    }, [showCallViaBody, assignTest, setValue]);

    const filteredCallerAgents = useMemo(() => {
        const lowerFilter = callerFilterText.toLowerCase();
        const list = callerFilterText
            ? agents.filter(
                  (agent) =>
                      agent.name?.toLowerCase().includes(lowerFilter) ||
                      agent.interfaceName?.toLowerCase().includes(lowerFilter)
              )
            : agents;
        // Match portal/ui `orderBy:'name'`
        return _.sortBy(list, (a) => a.name?.toLowerCase());
    }, [agents, callerFilterText]);

    const filteredCalleeAgents = useMemo(() => {
        const callerId = voipConfig?.caller?.id;
        const callerUseDirectCalling = _.get(voipConfig, 'caller.agent.voipConfiguration.useDirectCalling', false);
        const lowerFilter = calleeFilterText.toLowerCase();
        const list = agents.filter((agent) => {
            // Match portal/ui filterCalleeAgents: when a caller is selected, only allow
            // callee nPoints with the same direct-calling mode.
            const calleeUseDirectCalling = _.get(agent, 'useDirectCalling', false);
            const sameMode = _.isEmpty(callerId) ? true : callerUseDirectCalling === calleeUseDirectCalling;
            if (!sameMode) return false;
            if (!calleeFilterText) return true;
            return (
                agent.name?.toLowerCase().includes(lowerFilter) ||
                agent.interfaceName?.toLowerCase().includes(lowerFilter)
            );
        });
        // Match portal/ui `orderBy:'name'`
        return _.sortBy(list, (a) => a.name?.toLowerCase());
    }, [agents, calleeFilterText, voipConfig]);

    const callViaHead = watch(CALL_VIA_HEAD_FIELD);
    const callViaBody = watch(CALL_VIA_BODY_FIELD);
    const currentCallViaBody = showCallViaBody ? callViaBodyInput : callViaBody;
    const callViaBodyRequired = callViaHead === CALL_VIA_EXTENSION || callViaHead === CALL_VIA_PHONE_NUMBER;
    const callViaBodyHasError = callViaBodyRequired && !_.trim(currentCallViaBody);
    const calleeInstructionsHasError = voipConfig?.callee?.showDetails && (!callViaHead || callViaBodyHasError);

    const phoneRequired = !!voipConfig?.callee?.phone;
    const phoneFieldRules = useMemo(
        () => ({
            validate: (value) => (!phoneRequired || _.trim(String(value ?? '')) ? undefined : GENERIC_REQUIRED_MESSAGE),
        }),
        [phoneRequired]
    );
    const callViaBodyFieldRules = useMemo(
        () => ({
            validate: (value) =>
                !callViaBodyRequired || _.trim(String(value ?? '')) ? undefined : GENERIC_REQUIRED_MESSAGE,
        }),
        [callViaBodyRequired]
    );

    return (
        <>
            <div className={filterClasses.row}>
                <div className={filterClasses.column}>
                    <FormSection title={<Trans>Caller</Trans>}>
                        <div className={filterClasses.filterContainer}>
                            <TextField
                                placeholder="Filter nPoints"
                                size="small"
                                value={callerFilterText}
                                onChange={(e) => setCallerFilterText(e.target.value)}
                                fullWidth
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search style={{ fontSize: '18px' }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </div>
                        <div className={filterClasses.filterCount}>
                            <strong>{filteredCallerAgents.length}</strong> filtered nPoints
                        </div>
                        <Paper
                            style={{
                                width: '100%',
                                height: 230,
                                overflow: 'auto',
                                border: '1px solid rgba(0, 0, 0, 0.12)',
                            }}
                        >
                            <RadioGroup
                                style={{ paddingTop: 0, paddingBottom: 12, paddingLeft: 12, marginTop: -16 }}
                                value={selectedCallerId}
                                onChange={(e) => selectAgent(e, 'caller')}
                            >
                                {filteredCallerAgents.map((agent) => (
                                    <Tooltip key={agent.id} title={agent.interfaceTooltip} arrow>
                                        <FormControlLabel
                                            value={agent.id}
                                            label={
                                                <span className={filterClasses.agentLabel}>
                                                    <LegacyIcon icon={agent?.typeIcon} />
                                                    <span>{agent.name}</span>
                                                    {agent.adapterType === 'wireless' && (
                                                        <span>({agent.interfaceName} Interface)</span>
                                                    )}
                                                </span>
                                            }
                                            control={<Radio className={filterClasses.agentRadio} />}
                                            className={filterClasses.agentListItem}
                                        />
                                    </Tooltip>
                                ))}
                            </RadioGroup>
                        </Paper>
                    </FormSection>
                </div>
                <div className={filterClasses.column}>
                    <FormSection title={<Trans>Callee</Trans>}>
                        <div className={filterClasses.filterContainer}>
                            <TextField
                                placeholder="Filter nPoints"
                                size="small"
                                value={calleeFilterText}
                                onChange={(e) => setCalleeFilterText(e.target.value)}
                                fullWidth
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search style={{ fontSize: '18px' }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </div>
                        <div className={filterClasses.filterCount}>
                            <strong>{filteredCalleeAgents.length}</strong> filtered nPoints
                        </div>
                        <Paper
                            style={{
                                width: '100%',
                                height: 230,
                                overflow: 'auto',
                                border: '1px solid rgba(0, 0, 0, 0.12)',
                            }}
                        >
                            <RadioGroup
                                style={{ paddingBottom: 12 }}
                                value={selectedCalleeId}
                                onChange={(e) => selectAgent(e, 'callee')}
                            >
                                <FormControl
                                    component="fieldset"
                                    style={{ marginLeft: '16px', marginTop: '15px', marginBottom: '30px' }}
                                >
                                    <FormLabel component="legend" style={{ fontSize: '15px', color: '#000' }}>
                                        NON-NPOINT DEVICES
                                    </FormLabel>
                                    <FormControlLabel
                                        value={PHONE_ID}
                                        control={<Radio className={filterClasses.agentRadio} />}
                                        label={
                                            <span className={filterClasses.agentLabel}>
                                                <Trans>Phone</Trans>
                                            </span>
                                        }
                                        className={filterClasses.agentListItem}
                                    />
                                </FormControl>
                                <FormControl component="fieldset" style={{ marginLeft: '16px' }}>
                                    <FormLabel component="legend" style={{ fontSize: '15px', color: '#000' }}>
                                        NPOINTS
                                    </FormLabel>
                                    {filteredCalleeAgents.map((agent) => (
                                        <Tooltip key={agent.id} title={agent.interfaceTooltip} arrow>
                                            <FormControlLabel
                                                value={agent.id}
                                                label={
                                                    <span className={filterClasses.agentLabel}>
                                                        <LegacyIcon icon={agent?.typeIcon} />
                                                        <span>{agent.name}</span>
                                                        {agent.adapterType === 'wireless' && (
                                                            <span>({agent.interfaceName} Interface)</span>
                                                        )}
                                                    </span>
                                                }
                                                control={<Radio className={filterClasses.agentRadio} />}
                                                className={filterClasses.agentListItem}
                                            />
                                        </Tooltip>
                                    ))}
                                </FormControl>
                            </RadioGroup>
                        </Paper>
                        {voipConfig?.callee?.showDetails && (
                            <>
                                <div
                                    className={[
                                        filterClasses.calleeInstructionsSection,
                                        calleeInstructionsHasError
                                            ? filterClasses.calleeInstructionsSectionError
                                            : null,
                                    ]
                                        .filter(Boolean)
                                        .join(' ')}
                                >
                                    <FormSection title={<Trans>Callee Instructions</Trans>}>
                                        <NgpSelect
                                            fieldName={CALL_VIA_HEAD_FIELD}
                                            label={<Trans>Call this nPoint using the</Trans>}
                                            required={voipConfig?.callee?.showDetails}
                                            className={filterClasses.testSelect}
                                            onChangeFunc={(e) => changeCallVia(e, voipConfig.callee.agent)}
                                        >
                                            {callViaOptions.map((callVia) => (
                                                <MenuItem key={callVia.name} value={callVia.value}>
                                                    {callVia.name}
                                                </MenuItem>
                                            ))}
                                        </NgpSelect>

                                        {showCallViaBody && (
                                            <NgpTextField
                                                fieldName={CALL_VIA_BODY_FIELD}
                                                label={<Trans>{placeholder}</Trans>}
                                                disabled={disableCallViaBody}
                                                required={callViaBodyRequired}
                                                fieldRules={callViaBodyFieldRules}
                                                onChangeFunc={(e) => setCallViaBodyInput(e.target.value)}
                                                inputProps={{
                                                    size: 35,
                                                    maxLength: 150,
                                                    autoComplete: AUTO_COMPLETE_DEFAULT,
                                                }}
                                            />
                                        )}
                                    </FormSection>
                                </div>
                                {calleeInstructionsHasError && (
                                    <div className={filterClasses.calleeInstructionsErrorText}>
                                        Callee Instructions are required
                                    </div>
                                )}
                            </>
                        )}
                        {voipConfig?.callee?.phone && (
                            <NgpTextField
                                fieldName={PHONE_NUMBER_FIELD}
                                label={<Trans>Phone Number</Trans>}
                                required={phoneRequired}
                                fieldRules={phoneFieldRules}
                                inputProps={{
                                    size: 64,
                                    maxLength: 192,
                                    autoComplete: AUTO_COMPLETE_DEFAULT,
                                }}
                            />
                        )}
                    </FormSection>
                </div>
            </div>
            <VoipTestNowCallSettings config={voipConfig} testNow={testNow} codecs={codecs} />
        </>
    );
}
